import { createFileRoute } from '@tanstack/react-router'
import { Home, RefreshCw, Settings } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { InvoicesTab } from '@/features/billing/components/tabs/InvoicesTab'
import { OverviewTab } from '@/features/billing/components/tabs/OverviewTab'
import { UsageTab } from '@/features/billing/components/tabs/UsageTab'
import { billingService } from '@/features/billing/services/billingService'
import type { BillingOverview } from '@/features/billing/types/billing.types'
import { PageTitle } from '@/shared/components/common/PageTitle'
import {
  SubtleTabs,
  SubtleTabsContent,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

export const Route = createFileRoute('/_auth/settings/billing')({
  component: BillingSettingsPage,
})

function BillingSettingsPage() {
  const [billingData, setBillingData] = useState<BillingOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('overview')
  const { setBreadcrumbs } = useBreadcrumbs()

  const breadcrumbsContent = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <span className='flex items-center gap-1 text-muted-foreground'>
              <Settings className='w-4 h-4' />
              Settings
            </span>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Billing</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    []
  )

  const loadBillingData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await billingService.getBillingOverview()
      setBillingData(data)
    } catch (err) {
      toast.error(extractErrorMessage(err))
      setError('Failed to load billing information. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadBillingData()
  }, [loadBillingData])

  useEffect(() => {
    setBreadcrumbs(breadcrumbsContent)
  }, [setBreadcrumbs, breadcrumbsContent])

  if (loading) {
    return (
      <div className='container mx-auto py-6'>
        <div className='space-y-6'>
          {/* Page Title Skeleton */}
          <div className='space-y-2'>
            <Skeleton className='h-8 w-64' />
            <Skeleton className='h-4 w-96' />
          </div>

          {/* Tabs Skeleton */}
          <div className='space-y-6'>
            {/* Tab List Skeleton */}
            <div className='flex space-x-1 bg-muted p-1 rounded-lg w-fit'>
              <Skeleton className='h-9 w-20' />
              <Skeleton className='h-9 w-16' />
              <Skeleton className='h-9 w-20' />
            </div>

            {/* Tab Content Skeleton - Overview Layout */}
            <div className='space-y-6'>
              {/* Current Bill Card */}
              <div className='bg-transparent border rounded-lg p-6 space-y-4'>
                <Skeleton className='h-6 w-32' />
                <div className='space-y-2'>
                  <Skeleton className='h-8 w-24' />
                  <Skeleton className='h-4 w-48' />
                </div>
              </div>

              {/* Plan Cards Grid */}
              <div className='grid md:grid-cols-2 gap-6'>
                {/* Current Plan Card */}
                <div className='bg-transparent border rounded-lg p-6 space-y-4'>
                  <Skeleton className='h-6 w-28' />
                  <div className='space-y-3'>
                    <Skeleton className='h-5 w-32' />
                    <Skeleton className='h-4 w-40' />
                    <Skeleton className='h-4 w-36' />
                    <div className='flex justify-end'>
                      <Skeleton className='h-8 w-24' />
                    </div>
                  </div>
                </div>

                {/* Billing Contact Card */}
                <div className='bg-transparent border rounded-lg p-6 space-y-4'>
                  <Skeleton className='h-6 w-32' />
                  <div className='space-y-3'>
                    <Skeleton className='h-4 w-48' />
                    <Skeleton className='h-4 w-40' />
                    <Skeleton className='h-4 w-44' />
                    <div className='flex justify-end'>
                      <Skeleton className='h-8 w-36' />
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity Card */}
              <div className='bg-transparent border rounded-lg p-6 space-y-4'>
                <Skeleton className='h-6 w-36' />
                <div className='space-y-3'>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={i}
                      className='flex items-center justify-between py-2'
                    >
                      <div className='space-y-1'>
                        <Skeleton className='h-4 w-32' />
                        <Skeleton className='h-3 w-24' />
                      </div>
                      <Skeleton className='h-4 w-16' />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !billingData) {
    return (
      <div className='p-8'>
        <div className='text-center py-12'>
          <p className='text-red-600 mb-4'>
            {error || 'Billing data not found'}
          </p>
          <Button onClick={loadBillingData} variant='outline'>
            <RefreshCw className='mr-2 h-4 w-4' />
            Try again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className='container mx-auto py-6'>
      <div className='space-y-6'>
        <PageTitle
          title='Billing Settings'
          subtitle='Manage billing information, payment methods, and subscription details.'
        />

        <SubtleTabs value={activeTab} onValueChange={setActiveTab}>
          <SubtleTabsList>
            <SubtleTabsTrigger value='overview'>Overview</SubtleTabsTrigger>
            <SubtleTabsTrigger value='usage'>Usage</SubtleTabsTrigger>
            <SubtleTabsTrigger value='invoices'>Invoices</SubtleTabsTrigger>
          </SubtleTabsList>

          <SubtleTabsContent value='overview'>
            <OverviewTab
              billingData={billingData}
              onBillingDataChange={setBillingData}
            />
          </SubtleTabsContent>

          <SubtleTabsContent value='usage'>
            <UsageTab billingData={billingData} />
          </SubtleTabsContent>

          <SubtleTabsContent value='invoices'>
            <InvoicesTab billingData={billingData} />
          </SubtleTabsContent>
        </SubtleTabs>
      </div>
    </div>
  )
}
